import React from "react";
import { NavLink } from "react-router-dom";
import {
  BarChart03,
  BarChartSolid,
  Calendar,
  CalendarSolid,
  Inbox01,
  InboxSolid,
  Settings01,
  SettingsSolid,
  Sun,
  SunSolid,
  Users01,
  UsersSolid,
} from "@/components/icons";
import { cn } from "@/lib/utils";
import { IliaMark } from "@/components/askilia/atoms";
import { useStore } from "@/store/store";
import { useI18n } from "@/i18n";

export const NAV = [
  { to: "/today", labelKey: "nav.today", icon: Sun, activeIcon: SunSolid },
  { to: "/inbox", labelKey: "nav.inbox", icon: Inbox01, activeIcon: InboxSolid, badgeKey: "inbox" },
  { to: "/customers", labelKey: "nav.opportunities", mobileLabelKey: "nav.leads", icon: Users01, activeIcon: UsersSolid },
  { to: "/calendar", labelKey: "nav.calendar", icon: Calendar, activeIcon: CalendarSolid },
  { to: "/insights", labelKey: "nav.insights", icon: BarChart03, activeIcon: BarChartSolid },
  { to: "/settings", labelKey: "nav.settings", icon: Settings01, activeIcon: SettingsSolid },
];

const ROLE_KEYS = { Owner: "role.owner", Manager: "role.manager", Agent: "role.agent", Operator: "role.agent" };

function useInboxCount() {
  const { state } = useStore();
  return state.conversations.filter((c) => c.unread && !c.archivedAt).length;
}

export function Sidebar() {
  const { state } = useStore();
  const { t, localize } = useI18n();
  const inboxCount = useInboxCount();
  return (
    <aside className="hidden lg:flex lg:w-64 lg:flex-col lg:border-r lg:border-border lg:bg-surface">
      <div className="flex h-16 items-center gap-2.5 px-5">
        <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-border bg-white shadow-xs">
          <IliaMark size={26} />
        </span>
        <div className="leading-tight">
          <p className="text-[15px] font-semibold tracking-tight text-foreground">Askilia</p>
          <p className="text-[11px] text-muted-foreground">{localize(state.clinic.workspace)}</p>
        </div>
      </div>

      <nav className="flex-1 space-y-1 px-3 py-2">
        {NAV.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) =>
              cn(
                "group flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                isActive
                  ? "bg-primary-soft text-primary-soft-foreground"
                  : "text-muted-foreground hover:bg-muted hover:text-foreground"
              )
            }
          >
            {({ isActive }) => {
              const Icon = isActive ? item.activeIcon : item.icon;
              return (
                <>
                  <Icon className="h-[18px] w-[18px]" />
                  <span className="flex-1">{t(item.labelKey)}</span>
                  {item.badgeKey === "inbox" && inboxCount > 0 ? (
                    <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-accent px-1.5 text-[11px] font-semibold text-accent-foreground">
                      {inboxCount}
                    </span>
                  ) : null}
                </>
              );
            }}
          </NavLink>
        ))}
      </nav>

      <div className="border-t border-border p-3">
        <div className="flex items-center gap-3 rounded-lg px-2 py-2">
          <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-secondary text-sm font-semibold text-secondary-foreground">
            {state.user.initials}
          </span>
          <div className="min-w-0 flex-1 leading-tight">
            <p className="truncate text-sm font-medium text-foreground">{localize(state.user.name)}</p>
            <p className="text-xs text-muted-foreground">{t(ROLE_KEYS[state.user.role] || "role.agent")}</p>
          </div>
        </div>
      </div>
    </aside>
  );
}

export function BottomNav() {
  const inboxCount = useInboxCount();
  const { t } = useI18n();
  const items = NAV.slice(0, 5);
  return (
    <nav className="fixed inset-x-0 bottom-0 z-40 flex border-t border-border bg-surface/95 backdrop-blur lg:hidden">
      {items.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          className={({ isActive }) =>
            cn(
              "relative flex flex-1 flex-col items-center gap-0.5 py-2 text-[11px] font-medium",
              isActive ? "text-primary" : "text-muted-foreground"
            )
          }
        >
          {({ isActive }) => {
            const Icon = isActive ? item.activeIcon : item.icon;
            return (
              <>
                <Icon className="h-5 w-5" />
                {t(item.mobileLabelKey || item.labelKey)}
                {item.badgeKey === "inbox" && inboxCount > 0 ? (
                  <span className="absolute right-[22%] top-1 h-1.5 w-1.5 rounded-full bg-accent" />
                ) : null}
              </>
            );
          }}
        </NavLink>
      ))}
    </nav>
  );
}
